import React, { useState } from 'react';

import { useLisaExporterStatus } from '../../api/lisa';
import type { PmqRecord } from '../../interfaces/lisa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { formatUptime, formatDate, statusLabel } from './utils';

const INTERVAL_OPTIONS = [5, 10, 30, 60];

export default function ExporterStatus() {
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [intervalSeconds, setIntervalSeconds] = useState(10);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { records, counts, isLoading, refresh, process: proc } = useLisaExporterStatus(
        autoRefresh ? intervalSeconds * 1000 : 0
    );

    const { found, running, cpu, memoryMb, restarts, uptimeSince } = proc ?? {};

    const lastRecord = records[0] ?? null;
    const hasError = lastRecord !== null && lastRecord.status !== 'PROCESSED';

    const toggleExpand = (id: string) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    return (
        <div className={`lisa__processor${hasError ? ' lisa__processor--error' : ''}`}>
            <div className="lisa__ingestor__header">
                <h2 className="lisa__ingestor__title">Exporter</h2>
                {found && (
                    <div className="lisa__ingestor__process-controls">
                        <span className={`lisa__badge lisa__badge--process-${running ? 'online' : 'stopped'}`}>
                            {running ? 'Actief' : 'Gestopt'}
                        </span>
                        {running && uptimeSince && (
                            <span className="lisa__ingestor__process-stat" title="Uptime">⏱ {formatUptime(uptimeSince)}</span>
                        )}
                        {running && cpu !== null && (
                            <span className="lisa__ingestor__process-stat" title="CPU">CPU {cpu}%</span>
                        )}
                        {running && memoryMb !== null && (
                            <span className="lisa__ingestor__process-stat" title="RAM">{memoryMb} MB</span>
                        )}
                        {restarts != null && restarts > 0 && (
                            <span className="lisa__ingestor__process-stat lisa__ingestor__process-stat--warn" title="Restarts">↺ {restarts}</span>
                        )}
                    </div>
                )}
                <div className="lisa__refresh-controls">
                    <label className="lisa__refresh-toggle">
                        <input
                            type="checkbox"
                            checked={autoRefresh}
                            onChange={(e) => setAutoRefresh(e.target.checked)}
                        />
                        Auto-refresh
                    </label>
                    {autoRefresh && (
                        <select
                            className="lisa__interval-select"
                            value={intervalSeconds}
                            onChange={(e) => setIntervalSeconds(Number(e.target.value))}
                        >
                            {INTERVAL_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}s</option>
                            ))}
                        </select>
                    )}
                    <button className="lisa__refresh-btn" onClick={() => refresh()} title="Vernieuwen">
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                </div>
            </div>

            {Object.keys(counts).length > 0 && (
                <div className="lisa__status-cards">
                    {(['PENDING', 'PROCESSED', 'FAILED', 'DEAD_LETTER'] as const).map((status) => (
                        <div
                            key={status}
                            className={`lisa__status-card lisa__status-card--${status.toLowerCase().replaceAll('_', '-')}`}
                        >
                            <span className="lisa__status-card__label">{statusLabel(status)}</span>
                            <span className="lisa__status-card__count">{counts[status] ?? 0}</span>
                        </div>
                    ))}
                </div>
            )}

            {isLoading && <p className="lisa__loading">Laden…</p>}

            {!isLoading && records.length === 0 && (
                <p className="lisa__ingestor__empty">Nog geen verwerkte export-berichten.</p>
            )}

            {records.length > 0 && (
                <table className="lisa__ingestor__table">
                    <thead>
                        <tr>
                            <th className="lisa__processor__expand-cell"></th>
                            <th>Status</th>
                            <th>Type</th>
                            <th>Prio</th>
                            <th>Aangemaakt</th>
                            <th>Bijgewerkt</th>
                        </tr>
                    </thead>
                    <tbody>
                        {records.map((record: PmqRecord) => (
                            <React.Fragment key={record.id}>
                                <tr
                                    className="lisa__processor__row"
                                    onClick={() => toggleExpand(record.id)}
                                >
                                    <td className="lisa__processor__expand-cell">
                                        <span className={`lisa__processor__expand-icon${expandedId === record.id ? ' lisa__processor__expand-icon--open' : ''}`}>
                                            ▶
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`lisa__badge lisa__badge--${record.status.toLowerCase().replaceAll('_', '-')}`}>
                                            {statusLabel(record.status)}
                                        </span>
                                    </td>
                                    <td>{record.type}</td>
                                    <td>{record.prio}</td>
                                    <td>{formatDate(record.createdAt)}</td>
                                    <td>{formatDate(record.updatedAt)}</td>
                                </tr>
                                {expandedId === record.id && (
                                    <tr className="lisa__processor__detail-row">
                                        <td colSpan={6}>
                                            <div className="lisa__processor__detail">
                                                <div className="lisa__processor__detail__meta">
                                                    <div><strong>ID</strong><span>{record.id}</span></div>
                                                    <div><strong>Source ID</strong><span>{record.sourceId}</span></div>
                                                </div>
                                                <div className="lisa__processor__detail__payload">
                                                    <strong>Payload</strong>
                                                    <pre>{JSON.stringify(record.payload, null, 2)}</pre>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}
